<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

// Single "⋮" of the contacts header (the mockup keeps only search + "Nuevo
// contacto" in the top row): sort field, direction, import and export.
const props = defineProps({
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['import', 'export', 'update:sort']);

const { t } = useI18n();

const SORT_FIELDS = [
  ['name', 'NAME'],
  ['email', 'EMAIL'],
  ['company_name', 'COMPANY'],
  ['country', 'COUNTRY'],
  ['city', 'CITY'],
  ['last_activity_at', 'LAST_ACTIVITY'],
  ['created_at', 'CREATED_AT'],
];

const menuSections = computed(() => [
  {
    title: t('CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.LABEL'),
    items: SORT_FIELDS.map(([value, key]) => ({
      label: t(`CONTACTS_LAYOUT.HEADER.ACTIONS.SORT_BY.OPTIONS.${key}`),
      value,
      action: 'sort',
      icon: props.activeSort === value ? 'i-lucide-check' : 'i-lucide-minus',
      isSelected: props.activeSort === value,
    })),
  },
  {
    title: t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.LABEL'),
    items: [
      {
        label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.OPTIONS.ASCENDING'),
        value: 'asc',
        action: 'order',
        icon: 'i-lucide-arrow-up-narrow-wide',
        isSelected: props.activeOrdering === '',
      },
      {
        label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.ORDER.OPTIONS.DESCENDING'),
        value: 'desc',
        action: 'order',
        icon: 'i-lucide-arrow-down-wide-narrow',
        isSelected: props.activeOrdering === '-',
      },
    ],
  },
  {
    items: [
      {
        label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.IMPORT_CONTACT'),
        value: 'import',
        action: 'import',
        icon: 'i-lucide-download',
      },
      {
        label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.EXPORT_CONTACT'),
        value: 'export',
        action: 'export',
        icon: 'i-lucide-upload',
      },
    ],
  },
]);

const showActionsDropdown = ref(false);

const handleContactAction = ({ action, value }) => {
  if (action === 'sort') {
    emit('update:sort', { sort: value, order: props.activeOrdering });
    return;
  }
  if (action === 'order') {
    emit('update:sort', {
      sort: props.activeSort,
      order: value === 'desc' ? '-' : '',
    });
    return;
  }
  showActionsDropdown.value = false;
  if (action === 'import') emit('import');
  if (action === 'export') emit('export');
};
</script>

<template>
  <div v-on-clickaway="() => (showActionsDropdown = false)" class="relative">
    <Button
      icon="i-lucide-ellipsis-vertical"
      color="slate"
      variant="ghost"
      size="sm"
      :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
      @click="showActionsDropdown = !showActionsDropdown"
    />
    <DropdownMenu
      v-if="showActionsDropdown"
      :menu-sections="menuSections"
      class="ltr:right-0 rtl:left-0 mt-1 w-60 top-full"
      @action="handleContactAction($event)"
    />
  </div>
</template>
